/**
 * SaaS trial period — the frontend mirror of the wire contract.
 *
 * Source of truth: `backend/saas_trial.py` + `docs/payment-module/saas-trial-period.md`.
 * A change lands in all three artefacts in one pass or it is not a change.
 *
 * CONSTANTS ONLY (same rule as `affiliate/contract.js`): no shape assertions here. The
 * fixtures in `./fixtures.js` are for TESTS and are imported by no component.
 *
 * The frontend never does trial date arithmetic. `days_remaining`, `ends_at` and the
 * state come from the server, which owns the clock (the rule `BillingAdmin.jsx` already
 * states for the dunning window). The trial LENGTH is not spelled here either: it is
 * `trial_period_days` on `GET /api/saas/config`, so marketing copy interpolates it and a
 * literal "30" cannot drift from what Stripe is told.
 *
 * The closed sets are plain `export const NAME = [...]` arrays on purpose: the backend pin
 * test reads them with a regex, as `test_gateway_frontend_constants_pinned.py` does for
 * `gateway/contract.js`.
 */

// ── closed sets (mirror backend/saas_trial.py) ────────────────────────────────────

/**
 * `none`      — no window ever recorded.
 * `trialing`  — Stripe subscription is trialing (server-computed `days_remaining` present).
 * `ended`     — window closed, no paid invoice observed: elapsed (dunning owns the practice),
 *               or closed by Stripe before its recorded end. The latter carries
 *               `ended_early: true` with the recorded — still future — `ends_at`, so a reader
 *               never renders "ended on {ends_at}" for it (and never compares dates to find out).
 * `converted` — a real paid invoice closed the trial (`converted_at` present).
 */
export const TRIAL_STATES = ["none", "trialing", "ended", "converted"];

/**
 * Refusal vocabulary of the super-admin trial routes. `trialErrorText(e, ...)` maps a
 * member to `<prefix>.error.<CODE>` and anything else to `<prefix>.error.UNKNOWN`;
 * `detail.message` is English and is never rendered.
 */
export const TRIAL_ERROR_CODES = ["TRIAL_NOT_ACTIVE", "STRIPE_NOT_CONFIGURED", "STRIPE_ERROR"];

/** Bounds of a super-admin extension, in days (mirrors TRIAL_EXTEND_MIN/MAX_DAYS). */
export const TRIAL_EXTEND_MIN_DAYS = 1;
export const TRIAL_EXTEND_MAX_DAYS = 90;

// ── paths ─────────────────────────────────────────────────────────────────────────

/**
 * Every route a trial consumer calls, RELATIVE to the axios base (`lib/api.js` already
 * carries `/api`). Parameterised paths are functions so no id reaches a URL unencoded.
 */
export const TRIAL_PATHS = Object.freeze({
  // public — the trial length the marketing copy interpolates
  saasConfig: "/saas/config",

  // owner session — both carry the `trial` block
  myTenant: "/saas/my-tenant",
  billingSummary: "/tenant/billing/summary",

  // super admin — tenants.write
  superAdminExtend: (tenantId) =>
    `/super-admin/tenants/${encodeURIComponent(tenantId)}/trial/extend`,
  superAdminEnd: (tenantId) =>
    `/super-admin/tenants/${encodeURIComponent(tenantId)}/trial/end`,
});

export default {
  TRIAL_STATES,
  TRIAL_ERROR_CODES,
  TRIAL_EXTEND_MIN_DAYS,
  TRIAL_EXTEND_MAX_DAYS,
  TRIAL_PATHS,
};
